import React from 'react'
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Paper,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import WcIcon from '@mui/icons-material/Wc'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoIcon from '@mui/icons-material/Info'
import FlagIcon from '@mui/icons-material/Flag'
import BadgeIcon from '@mui/icons-material/Badge'

// Sub-components
const EditableField = React.memo(
  ({ label, value, onChange, icon: Icon, type = 'text', selectOptions, fullWidth = true, required }) => {
    return (
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center" mb={{ xs: 0.75, sm: 1 }}>
          {Icon && (
            <Icon
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                color: 'text.secondary',
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
            }}>
            {label} {required && '*'}
          </Typography>
        </Stack>
        {selectOptions ? (
          <FormControl fullWidth size="small">
            <Select value={value || ''} onChange={onChange} displayEmpty>
              {selectOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            value={value || ''}
            onChange={onChange}
            type={type}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />
        )}
      </Box>
    )
  }
)

EditableField.displayName = 'EditableField'

const InfoField = React.memo(({ label, value, icon: Icon, warning, monospace, sx }) => {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" mb={{ xs: 0.75, sm: 1 }}>
        {Icon && (
          <Icon
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              color: warning ? 'error.main' : 'text.secondary',
            }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: warning ? 'error.main' : 'text.secondary',
            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
          }}>
          {label}
        </Typography>
      </Stack>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 1.5, md: 1.75 },
          borderRadius: 1.5,
          backgroundColor: (theme) =>
            warning
              ? theme.palette.mode === 'light'
                ? 'rgba(244, 67, 54, 0.08)'
                : 'rgba(244, 67, 54, 0.16)'
              : theme.palette.mode === 'light'
                ? 'rgba(249, 250, 251, 0.5)'
                : 'rgba(18, 18, 18, 0.5)',
          border: (theme) =>
            warning
              ? `1px solid ${theme.palette.mode === 'light' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.5)'}`
              : 'none',
          ...sx,
        }}>
        <Typography
          sx={{
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            color: 'text.primary',
            fontFamily: monospace ? 'monospace' : 'inherit',
            fontWeight: monospace ? 600 : 400,
          }}>
          {value || '-'}
        </Typography>
      </Paper>
    </Box>
  )
})

InfoField.displayName = 'InfoField'

/**
 * ParticipantEditForm Component
 *
 * Form component for editing participant information
 *
 * @param {Object} props
 * @param {Object} props.formState - Current form state values
 * @param {Function} props.setFormState - Function to update form state
 * @param {Object} props.participant - Participant object (for read-only fields)
 * @param {Array<string>} props.genderList - List of gender options
 */
const ParticipantEditForm = ({ formState, setFormState, participant, genderList }) => {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 2, sm: 2.5, md: 3 }}
      sx={{ alignItems: 'flex-start' }}>
      {/* Left Column */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
        <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          <EditableField
            label="BIB"
            value={formState.bib}
            onChange={(e) => setFormState({ bib: e.target.value })}
            icon={ConfirmationNumberIcon}
            required
          />
          <EditableField
            label="First Name"
            value={formState.firstname}
            onChange={(e) => setFormState({ firstname: e.target.value })}
            icon={PersonIcon}
            required
          />
          <EditableField
            label="Last Name"
            value={formState.lastname}
            onChange={(e) => setFormState({ lastname: e.target.value })}
            icon={PersonIcon}
            required
          />
          <EditableField
            label="Display Name"
            value={formState.displayName}
            onChange={(e) => setFormState({ displayName: e.target.value })}
            icon={PersonIcon}
          />
          <EditableField
            label="Gender"
            value={formState.gender}
            onChange={(e) => setFormState({ gender: e.target.value })}
            icon={WcIcon}
            selectOptions={genderList}
            required
          />
          <EditableField
            label="Age Category"
            value={formState.ageCategory}
            onChange={(e) => setFormState({ ageCategory: e.target.value })}
            icon={CalendarTodayIcon}
            required
          />
        </Stack>
      </Box>

      {/* Right Column */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
        <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          <EditableField
            label="Row"
            value={formState.row}
            onChange={(e) => setFormState({ row: e.target.value })}
            icon={InfoIcon}
            required
          />
          <EditableField
            label="Status"
            value={formState.status}
            onChange={(e) => setFormState({ status: e.target.value })}
            icon={CheckCircleIcon}
            required
          />
          <EditableField
            label="Nationality"
            value={formState.nationality}
            onChange={(e) => setFormState({ nationality: e.target.value })}
            icon={FlagIcon}
            required
          />
          <EditableField
            label="National ID"
            value={formState.nationalId}
            onChange={(e) => setFormState({ nationalId: e.target.value })}
            icon={BadgeIcon}
            required
          />
          {/* Read-only field for check-in status */}
          <InfoField
            label="Check-in Status"
            value={participant?.lastActivity?.status || 'Not Checked In'}
            icon={CheckCircleIcon}
          />
        </Stack>
      </Box>
    </Stack>
  )
}

// Export sub-components for use in other files
export { EditableField, InfoField }

export default React.memo(ParticipantEditForm)
